import { Injectable, inject } from '@angular/core';
import { AlertController, AlertOptions, LoadingController, LoadingOptions } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class LoadingService {
  private readonly loadingController = inject(LoadingController);
  private readonly translate = inject(TranslateService);
  private readonly alertController = inject(AlertController);

  private activeLoadingTasks: number = 0;
  private timeoutAllowAbort: number = 8_000; // 8 seconds
  private loadingElement: HTMLIonLoadingElement;
  private timerLoadingAbort: ReturnType<typeof setTimeout> | null = null;
  private alertElement: HTMLIonAlertElement;

  public async start(): Promise<void> {
    if (typeof window === 'undefined') return;

    this.incrementLoadingTasks();

    if (this.activeLoadingTasks == 1 && !this.loadingElement) {
      await this.showLoaderWithTimer();
    }
  }

  public async stop(): Promise<void> {
    if (typeof window === 'undefined') return;

    this.decrementLoadingTasks();

    if (this.activeLoadingTasks == 0) {
      if (this.loadingElement) {
        await this.deleteLoadingElement();
      }
      if (this.alertElement) {
        await this.deleteAlertElement();
      }
    }
  }

  private createLoaderElement(): Promise<HTMLIonLoadingElement> {
    const options: LoadingOptions = {
      message: this.getLoaderMessage(),
      backdropDismiss: false,
      cssClass: 'loader-transparent',
    };

    return this.loadingController.create(options);
  }

  private getLoaderMessage(): string {
    const translatedMessage: string = this.translate.instant('loading');
    return (translatedMessage !== 'loading') ? translatedMessage : 'Loading...';
  }

  private addAbortListeners(): void {
    if (!this.loadingElement) return;

    this.loadingElement.backdropDismiss = true;

    const listener = async (): Promise<void> => {
      if (this.activeLoadingTasks > 0) {
        await this.showAlertElement();
      } else {
        await this.deleteLoadingElement();
      }
    };

    this.loadingElement.addEventListener('ionBackdropTap', listener, {once: true});
    this.loadingElement.addEventListener('click', listener, {once: true});
  }

  private async showLoadingElement(): Promise<void> {
    if (this.loadingElement) return;

    try {
      this.loadingElement = await this.createLoaderElement();
      if (this.activeLoadingTasks > 0) {
        await this.loadingElement?.present();
      } else {
        await this.deleteLoadingElement();
      }
    } catch (error) {
      throw new Error('Error show loading element', {cause: error});
    }
  }

  private async showLoaderWithTimer(): Promise<void> {
    await this.showLoadingElement();

    if (this.timerLoadingAbort) {
      clearTimeout(this.timerLoadingAbort);
    }
    this.timerLoadingAbort = setTimeout((): void => {
      if (this.loadingElement) {
        this.addAbortListeners();
      }
    }, this.timeoutAllowAbort);
  }

  private async showLoaderWithoutTimer(): Promise<void> {
    await this.deleteAlertElement(); // it's necessary!
    await this.showLoadingElement();

    if (this.loadingElement) {
      this.addAbortListeners();
    }
  }

  private async deleteLoadingElement(): Promise<void> {
    if (this.timerLoadingAbort) {
      clearTimeout(this.timerLoadingAbort);
      this.timerLoadingAbort = null;
    }

    if (this.loadingElement) {
      try {
        await this.loadingElement.dismiss();
      } catch (error) {
        throw new Error('Error deleting loading element', {cause: error});
      } finally {
        this.loadingElement = null;
      }
    }
  }

  private createAlertElement(): Promise<HTMLIonAlertElement> {
    const opts: AlertOptions = {
      header: this.translate.instant('loadingService.timeoutRequest.alert.header'),
      message: this.translate.instant('loadingService.timeoutRequest.alert.message'),
      cssClass: 'alert-loader-abort',
      backdropDismiss: false,
      buttons: [
        {
          text: this.translate.instant('loadingService.timeoutRequest.alert.buttons.cancel'),
          role: 'cancel',
          handler: async () => {
            if (this.activeLoadingTasks > 0) {
              await this.showLoaderWithoutTimer();
            } else {
              await this.deleteAlertElement();
            }
          }
        },
        {
          text: this.translate.instant('loadingService.timeoutRequest.alert.buttons.destructive'),
          role: 'destructive',
          handler: async () => {
            await this.deleteAlertElement();
          }
        }
      ]
    };

    return this.alertController.create(opts);
  }

  private async showAlertElement(): Promise<void> {
    if (!this.loadingElement || this.alertElement) return;

    try {
      this.alertElement = await this.createAlertElement();
      await this.deleteLoadingElement();

      if (this.activeLoadingTasks > 0) {
        await this.alertElement?.present();
      } else {
        await this.deleteAlertElement();
      }
    } catch (error) {
      throw new Error('Error show alert element', {cause: error});
    }
  }

  private async deleteAlertElement(): Promise<void> {
    if (!this.alertElement) return;

    try {
      await this.alertElement.dismiss();
    } catch (error) {
      throw new Error('Error deleting alert element', {cause: error});
    } finally {
      this.alertElement = null;
    }
  }

  private incrementLoadingTasks() {
    this.activeLoadingTasks++;
  }

  private decrementLoadingTasks() {
    if (this.activeLoadingTasks > 0) {
      this.activeLoadingTasks--;
    }
  }
}
